import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Container, InputGroup, Row, Spinner, DropdownButton, Dropdown, Badge } from "react-bootstrap";
import SearchBar from "../ui/SearchBar";
import {
  fetchJobs,
  fetchPrograms,
  fetchUserCasesLazy,
  fetchUserCasesMy,
  fetchUserCasesImages,
  fetchCasesActivity,
} from "../../services/httpClient";
import { Case } from "../../types/case";
import useApp from "../../utils/useApp";
import CaseView from "./CaseView";
import PeopleView from "./PeopleView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faBriefcase,
  faClose,
  faFilter,
  faFolderOpen,
  faList,
  faSquareXmark,
  faUserFriends,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import MyCasesToday from "./MyCasesToday";
import MyCasesActivity from "./MyCasesActivity";

const PAGE_SIZE = 150; //Note: maximum amount on backend will have to be changed or removed to do above 150 at a time
const CASE_LOAD_AMOUNT = 40;
const PEOPLE_LOAD_AMOUNT = 40;

interface MyCasesState {
  limitedAccess: boolean;
}

const MyCases = ({ limitedAccess }: MyCasesState) => {
  const { auth, showMenu, viewAllCases, setViewAllCases, viewedActivityDate, setViewedActivityDate }: any = useApp();
  const [isLoadingActive, setIsLoadingActive] = useState<boolean>(true);
  const [isLoadingAll, setIsLoadingAll] = useState<boolean>(true);
  const [isMainLoading, setIsMainLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const [viewActive, setViewActive] = useState<boolean>(true);
  const [activeCases, setActiveCases] = useState<Case[]>([]);
  const [inactiveCases, setInactiveCases] = useState<Case[]>([]);
  const [myCases, setMyCases] = useState<Case[]>([]);
  const [otherCases, setOtherCases] = useState<Case[]>([]);
  const [filteredMyCases, setFilteredMyCases] = useState<Case[]>([]);
  const [filteredOtherCases, setFilteredOtherCases] = useState<Case[]>([]);
  const [casesFiltered, setCasesFiltered] = useState<boolean>(false);

  const [viewByCase, setViewByCase] = useState<boolean>(true);
  const [showActivity, setShowActivity] = useState<boolean>(false);
  const [viewActivityIndicator, setViewActivityIndicator] = useState<boolean>(false);
  const [latestActivityDate, setLatestActivityDate] = useState<Date | null>(viewedActivityDate ? viewedActivityDate : new Date(0));
  const [activity, setActivity] = useState<any>({} as any);
  const [caseFilter, setCaseFilter] = useState<string>("");

  const [allPeople, setAllPeople] = useState<any[]>([]);
  const [myPeople, setMyPeople] = useState<any[]>([]);
  const [filteredAllPeople, setFilteredAllPeople] = useState<any[]>([]);
  const [filteredMyPeople, setFilteredMyPeople] = useState<any[]>([]);
  const [peopleFiltered, setPeopleFiltered] = useState<boolean>(false);

  const [banFilterClicked, setBanFilterClicked] = useState<boolean>(false);
  const [peopleFilter, setPeopleFilter] = useState<string>("");

  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);

  const [totalCases, setTotalCases] = useState<number>(0);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [otherCasesLoaded, setOtherCasesLoaded] = useState<number>(CASE_LOAD_AMOUNT);
  const [peopleLoaded, setPeopleLoaded] = useState<number>(PEOPLE_LOAD_AMOUNT);

  let people: any = [];
  let myCasePeople: any = [];

  useEffect(() => {
    // trackDataEvent("filter", "mycase_search");
    setOtherCasesLoaded(CASE_LOAD_AMOUNT);
    setPeopleLoaded(PEOPLE_LOAD_AMOUNT);
  }, [searchText]);

  useEffect(() => {
    getPrograms();
    getDepartments();
    if (limitedAccess) {
      setViewAllCases(true);
    }
    setActiveCases([]);
    fetchAllPages(1, []);
    getActivity();
  }, [auth?.profile]);

  const fetchAllPages = async (startPage: number, prevCases: Case[]) => {
    let allCases = [...prevCases];
    let loaded = new Set<number>();
    let fetching = new Set<number>();
    let isDone = false;

    // fetch 'my cases' then process
    const fetchMyCases = async () => {
      fetchUserCasesMy()
        .then((res: any) => {
          const cases = res[0].data.data;
          // Merge new cases, avoiding duplicates by id
          allCases = [...allCases, ...cases.filter((c: any) => !allCases.some((ac) => ac.id === c.id))];
          setActiveCases([...allCases]);

          if (auth?.profile?.role === "support") {
            const mycases = allCases.filter((c: any) => c.support[0]?.user_id === auth.profile.id);
            setMyCases(mycases);
            setFilteredMyCases(mycases);

            const myPpl = mycases
              .flatMap((caseObj: any) => [
                ...caseObj.family.map((f: any) => ({ ...f, name: f.user_name, case_id: caseObj.id })),
                ...caseObj.members.map((m: any) => ({ ...m, name: m.member_name, case_id: caseObj.id })),
              ])
              .sort((a: any, b: any) => {
                const nameA = a.name.replace(/\s/g, "");
                const nameB = b.name.replace(/\s/g, "");
                return nameA.localeCompare(nameB);
              });
            setMyPeople(myPpl);
            setFilteredMyPeople(myPpl);
          } else {
            performFiltering(allCases);
          }
        })
        .finally(() => {
          setIsLoadingActive(false);
        });
    };

    // fetch a single page and process results
    const fetchPage = async (pageNum: number) => {
      if (loaded.has(pageNum) || fetching.has(pageNum) || isDone) return;
      fetching.add(pageNum);

      fetchUserCasesLazy("active", PAGE_SIZE, pageNum)
        .then((res: any) => {
          const meta = res[0]?.data.meta;
          setTotalCases(meta.total);
          const cases = res[0]?.data?.data || [];
          loaded.add(pageNum);
          fetching.delete(pageNum);

          // Merge new cases, avoid dupes
          allCases = [...allCases, ...cases.filter((c: any) => !allCases.some((ac) => ac.id === c.id))];
          setActiveCases([...allCases]);

          if (auth?.profile?.role === "support") {
            const mycases = allCases.filter((c: any) => c.support[0]?.user_id === auth.profile.id);
            setMyCases(mycases);
            setFilteredMyCases(mycases);

            const myPpl = mycases
              .flatMap((caseObj: any) => [
                ...caseObj.family.map((f: any) => ({ ...f, name: f.user_name, case_id: caseObj.id })),
                ...caseObj.members.map((m: any) => ({ ...m, name: m.member_name, case_id: caseObj.id })),
              ])
              .sort((a: any, b: any) => {
                const nameA = a.name.replace(/\s/g, "");
                const nameB = b.name.replace(/\s/g, "");
                return nameA.localeCompare(nameB);
              });
            setMyPeople(myPpl);
            setFilteredMyPeople(myPpl);
          } else {
            performFiltering(allCases);
          }

          // If there are more pages, try to fetch the next page
          if (pageNum !== meta.last_page) {
            fetchPage(pageNum + 1);
          } else {
            isDone = true;
            setIsMainLoading(false);
            loadImages();
          }
        })
        .catch((err: any) => {
          console.log(err.response.data);
          fetching.delete(pageNum);
        })
        .finally(() => {
          setIsLoadingActive(false);
        });
    };

    // load and assign profile images
    function loadImages() {
      const familyIDs = people.filter((p: any) => p.user_id != null).map((p: any) => p.user_id);
      const memberIDs = people.filter((p: any) => p.id != null).map((p: any) => p.id);
      fetchUserCasesImages(familyIDs, memberIDs)
        .then((res: any) => {
          const familyImages = res[0].data.data_fam || [];
          const memberImages = res[0].data.data_memb || [];
          const images = [...familyImages, ...memberImages];
          const peopleMap = Object.fromEntries(
            people.map((p: any) => {
              const key = p.id != null ? `mid:${p.id}` : `fid:${p.user_id}`;
              return [key, p];
            }),
          );

          // Set Images for All People
          images.forEach((image: any) => {
            const userKey = image.user_id != null ? `fid:${image.user_id}` : null;
            const memberKey = image.member_id != null ? `mid:${image.member_id}` : null;
            const targetKey = userKey || memberKey;
            if (image.data && targetKey && peopleMap[targetKey]) {
              peopleMap[targetKey].profile = [{ id: image.id, data: image.data }];
            }
          });
          setAllPeople(people);
          setFilteredAllPeople(people);
          people.forEach((person: any) => {
            if (!person.profile) return;
            const caseItem = allCases.find((c: any) => c.id === person.case_id);
            if (!caseItem) return;

            // Update family array
            if (caseItem.family && Array.isArray(caseItem.family)) {
              caseItem.family = caseItem.family.map((user: any) => (user.user_id === person.user_id ? person : user));
            }
            // Update members array
            if (caseItem.members && Array.isArray(caseItem.members)) {
              caseItem.members = caseItem.members.map((member: any) => (member.id === person.id ? person : member));
            }
          });
          // This makes the image stay through filtering
          setActiveCases(allCases);
          // Set Images for My People
          const filteredList = myCasePeople.map((person: any) => {
            const match = people.find((personWithImage: any) => {
              return person.id != null ? personWithImage.id === person.id : personWithImage.user_id === person.user_id;
            });
            return match ? { ...person, ...match } : person;
          });
          setMyPeople(filteredList);
          setFilteredMyPeople(filteredList);
        })
        .finally(() => {
          loadInactive(1);
        });
    }

    // Start with My Cases if viewing my cases
    if (!viewAllCases) {
      await fetchMyCases();
    }
    // then with the first page
    fetchPage(startPage);
  };

  //variable to check if viewing active that works while stuff is loading
  const viewActiveRef = useRef(viewActive);

  useEffect(() => {
    viewActiveRef.current = viewActive;
  }, [viewActive]);

  const loadInactive = async (startPage: number) => {
    let loaded = new Set<number>();
    let fetching = new Set<number>();
    let isDone = false;
    let inactivePeople: any = [];
    let allInactiveCases: any[] = [];
    let inactiveCasesVar: any[] = [];

    const fetchPage = async (pageNum: number) => {
      if (loaded.has(pageNum) || fetching.has(pageNum) || isDone) return;
      fetching.add(pageNum);

      fetchUserCasesLazy("inactive", PAGE_SIZE, pageNum)
        .then((res: any) => {
          const meta = res[0]?.data.meta;
          const cases = res[0]?.data?.data || [];

          // Merge new cases, avoiding duplicates by id
          allInactiveCases = [...allInactiveCases, ...cases.filter((c: any) => !allInactiveCases.some((ac) => ac.id === c.id))];
          inactiveCasesVar = allInactiveCases;
          loaded.add(pageNum);
          fetching.delete(pageNum);

          setInactiveCases([...allInactiveCases]);
          // get the family and members for inactive cases
          cases.forEach((aCase: any) => {
            inactivePeople.push(...aCase.family);
            inactivePeople.push(...aCase.members);
          });

          // If there are more pages, try to fetch the next page
          if (pageNum !== meta.last_page) {
            setIsMainLoading(false);
            if (!viewActiveRef.current) {
              performFiltering(allInactiveCases);
            }
            fetchPage(pageNum + 1);
          } else {
            if (!viewActiveRef.current) {
              performFiltering(allInactiveCases);
            }
            isDone = true;
            setIsLoadingAll(false);
            loadInactiveImages();
          }
        })
        .catch((err: any) => {
          console.log(err.response.data);
          fetching.delete(pageNum);
        });
      return;
    };

    // load and assign profile images
    function loadInactiveImages() {
      const familyIDs = inactivePeople.filter((p: any) => p.user_id != null).map((p: any) => p.user_id);
      const memberIDs = inactivePeople.filter((p: any) => p.id != null).map((p: any) => p.id);

      fetchUserCasesImages(familyIDs, memberIDs).then((res: any) => {
        const familyImages = res[0].data.data_fam || [];
        const memberImages = res[0].data.data_memb || [];
        const images = [...familyImages, ...memberImages];

        const peopleMap = Object.fromEntries(
          inactivePeople.map((p: any) => {
            const key = p.id != null ? `mid:${p.id}` : `fid:${p.user_id}`;
            return [key, p];
          }),
        );
        // Set Images for Inactive People
        images.forEach((image: any) => {
          const userKey = image.user_id != null ? `fid:${image.user_id}` : null;
          const memberKey = image.member_id != null ? `mid:${image.member_id}` : null;
          const targetKey = userKey || memberKey;
          if (image.data && targetKey && peopleMap[targetKey]) {
            peopleMap[targetKey].profile = [{ id: image.id, data: image.data }];
          }
        });
        inactivePeople = [...Object.values(peopleMap)];
        inactivePeople.forEach((person: any) => {
          if (!person.profile) return;
          const caseItem = inactiveCasesVar.find((c: any) => c.id === person.case_id);
          if (!caseItem) return;

          // Update family array
          if (caseItem.family && Array.isArray(caseItem.family)) {
            caseItem.family = caseItem.family.map((user: any) => (user.user_id === person.user_id ? person : user));
          }
          // Update members array
          if (caseItem.members && Array.isArray(caseItem.members)) {
            caseItem.members = caseItem.members.map((member: any) => (member.id === person.id ? person : member));
          }
        });
        setInactiveCases(inactiveCasesVar);
        if (!viewActiveRef.current) {
          performFiltering(inactiveCasesVar);
        }
      });
    }

    fetchPage(startPage);
  };

  function performFiltering(cases: Case[]) {
    const mycases = cases.filter((c: any) => c.primaryCM?.user_id === auth.profile.id || c.secondaryCM?.user_id === auth.profile.id);
    setMyCases(mycases);

    const othercases = cases.filter((c: any) => !mycases.some((ci: any) => ci.id === c.id));
    setOtherCases(othercases);

    setFilteredMyCases(mycases);
    setFilteredOtherCases(othercases);
    const myPpl = mycases
      .flatMap((caseObj: any) => [
        ...caseObj.family.map((f: any) => ({ ...f, name: f.user_name, case_id: caseObj.id })),
        ...caseObj.members.map((m: any) => ({ ...m, name: m.member_name, case_id: caseObj.id })),
      ])
      .sort((a: any, b: any) => a.last_name?.localeCompare(b.last_name));
    myCasePeople = myPpl;
    setMyPeople(myPpl);
    setFilteredMyPeople(myPpl);
    const allPpl = cases
      .flatMap((caseObj: any) => [
        ...caseObj.family.map((f: any) => ({ ...f, name: f.user_name, case_id: caseObj.id })),
        ...caseObj.members.map((m: any) => ({ ...m, name: m.member_name, case_id: caseObj.id })),
      ])
      .sort((a: any, b: any) => a.last_name?.localeCompare(b.last_name));
    people = allPpl;
    setAllPeople(allPpl);
    setFilteredAllPeople(allPpl);
  }

  const getPrograms = () => {
    fetchPrograms().then((res: any) => {
      const progs = res[0]?.data ?? [];

      const flattenedProgs = progs.flatMap((prog: any) => {
        const levels = prog.programLevels ?? [];

        const levelEntries = levels.map((level: any) => ({
          ...prog,
          id: `${prog.id}-${level.id}`,
          level_id: level.id,
          program_id: prog.id,
          name: `${prog.name} - ${level.name}`,
          key: `${prog.id}-${level.id}`,
        }));

        // Always include the base program
        const baseProgramEntry = {
          ...prog,
          id: prog.id,
          name: prog.name,
          key: prog.id,
        };

        return [baseProgramEntry, ...levelEntries];
      });
      setPrograms(flattenedProgs);
    });
  };

  const getDepartments = () => {
    fetchJobs()
      .then((res: any) => {
        const jobs = res[0].data;
        //group by departments
        const depts = Array.from(new Set(jobs.map((job: any) => job.department))).map((dept: any) => ({ name: dept, key: dept }));
        setDepartments(depts);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getActivity = () => {
    fetchCasesActivity().then((res: any) => {
      const acts = res[0]?.data ?? {};
      const viewedDate = new Date(viewedActivityDate);

      // Add isNew flag to each entry within each group
      const actsWithNewFlag = Object.fromEntries(
        Object.entries(acts).map(([key, entries]: [string, any]) => [
          key,
          entries.map((entry: any) => ({
            ...entry,
            isNew: new Date(entry.activity_feed_at) > viewedDate,
          })),
        ]),
      );
      setActivity(actsWithNewFlag);

      const allActivity = Object.values(actsWithNewFlag).flat() as any[];
      const hasNewActivity = allActivity.some((entry: any) => entry.isNew);

      const mostRecent = allActivity.reduce((max: Date, entry: any) => {
        const current = new Date(entry.activity_feed_at);
        return current > max ? current : max;
      }, new Date(0));

      const now = new Date();
      const adjustedNow = new Date(now.getTime() + now.getTimezoneOffset() * 60000);

      if (hasNewActivity) {
        setViewActivityIndicator(true);
        setLatestActivityDate(mostRecent);
      } else if (mostRecent < adjustedNow) {
        setLatestActivityDate(adjustedNow);
      }
    });
  };

  const onClickViewCases = () => {
    if (viewAllCases) {
      setViewAllCases(false);
      setFilteredMyCases(myCases);
      // trackNavigationEvent("switch_view", "view_my_cases");
    } else {
      setViewAllCases(true);
      setFilteredMyCases(myCases);
      setFilteredOtherCases(otherCases);
      // trackNavigationEvent("switch_view", "view_all_cases");
    }

    clearFilters();
  };

  const onPerformSearchLogicByCase = (text: string) => {
    setCaseFilter("");
    const lowerText = text.toLowerCase();
    var finalMyCases = casesFiltered ? filteredMyCases : myCases;
    setFilteredMyCases(searchLogicByCase(lowerText, finalMyCases));

    var finalOtherCases = casesFiltered ? filteredOtherCases : otherCases;
    setFilteredOtherCases(searchLogicByCase(lowerText, finalOtherCases));
  };

  const searchLogicByCase = (text: string, searchObject: any) => {
    //filter by name, user, and member of case
    var myFiltered: any = searchObject?.filter((item: any) => {
      return (
        item.name.toLowerCase().match(text) ||
        item?.family.some((fam: any) => fam?.user_name.toLowerCase().match(text)) ||
        item?.members.some((mem: any) => mem?.member_name.toLowerCase().match(text))
      );
    });
    return myFiltered;
  };

  const onPerformSearchLogicByPerson = (text: string) => {
    setBanFilterClicked(false);
    setPeopleFilter("");
    const lowerText = text.toLowerCase();
    var finalMyPpl = peopleFiltered ? filteredMyPeople : myPeople;
    setFilteredMyPeople(searchLogicByPeople(lowerText, finalMyPpl));

    var finalAllPpl = peopleFiltered ? filteredAllPeople : allPeople;
    setFilteredAllPeople(searchLogicByPeople(lowerText, finalAllPpl));
  };

  const searchLogicByPeople = (text: string, searchObject: any) => {
    //filter by name of person
    var myFiltered: any = searchObject?.filter((item: any) => {
      return item.name.toLowerCase().match(text);
    });
    return myFiltered;
  };

  const onClickViewActive = (active: boolean) => {
    setOtherCasesLoaded(CASE_LOAD_AMOUNT);
    setViewActive(active);
    setViewAllCases(true);
    clearFilters();

    if (active) {
      performFiltering(activeCases);
    } else {
      performFiltering(inactiveCases);
    }

    // if (active === false) {
    //   trackNavigationEvent("switch_view", "view_inactive");
    // }
  };

  const onClickViewBy = (byCase: boolean) => {
    setViewByCase(byCase);

    clearFilters();
    setFilteredMyCases(myCases);
    setFilteredOtherCases(otherCases);
    setFilteredMyPeople(myPeople);
    setFilteredAllPeople(allPeople);

    // if (byCase === false) {
    //   trackNavigationEvent("switch_view", "view_by_people");
    // }
  };

  const onClickShowActivity = () => {
    setShowActivity((prev) => !prev);
    setViewActivityIndicator(false);
    setViewedActivityDate(latestActivityDate);
  };

  const onClickFilterCase = (type: string) => {
    setSearchText("");
    setShowActivity(false);

    if (type === caseFilter) {
      clearFilters();
      return;
    }
    setCaseFilter(type);

    var myfCases = [] as any[];
    var otherfCases = [] as any;

    switch (type) {
      case "assessment":
        myfCases = myCases.filter((c: any) => {
          return c.assessment_due;
        });
        otherfCases = otherCases.filter((c: any) => {
          return c.assessment_due;
        });
        break;
      case "messages":
        myfCases = myCases.filter((c: any) => {
          return c.new_messages;
        });
        otherfCases = otherCases.filter((c: any) => {
          return c.new_messages;
        });
        break;
      case "task":
        myfCases = myCases.filter((c: any) => {
          return c.task_review;
        });
        otherfCases = otherCases.filter((c: any) => {
          return c.task_review;
        });
        break;
      default:
        break;
    }

    setFilteredMyCases(myfCases);
    setFilteredOtherCases(otherfCases);
  };

  const onClickFilterBan = () => {
    if (banFilterClicked) {
      clearFilters();
    } else {
      setPeopleFilter("");
      setSelectedPrograms([]);
      setSearchText("");

      setBanFilterClicked(true);
      var myPpl = myPeople;
      var allPpl = allPeople;

      myPpl = myPpl.filter((m: any) => m.is_banned === 1);
      allPpl = allPpl.filter((m: any) => m.is_banned === 1);

      setFilteredMyPeople(myPpl);
      setFilteredAllPeople(allPpl);
    }
  };

  const onClickFilterBy = (values: any) => {
    var selected: any;
    var myPpl;
    var allPpl;

    switch (peopleFilter) {
      case "program":
        setSelectedPrograms(values);
        selected = programs.filter((p) => values.includes(p.key));

        if (selected.length > 0) {
          setBanFilterClicked(false);
          setSearchText("");

          setPeopleFiltered(true);
          myPpl = myPeople;
          allPpl = allPeople;

          var progIDs: any = [];
          var levelIDs: any = [];
          selected.forEach((selectedProg: any) => {
            if (selectedProg?.level_id) {
              levelIDs.push(selectedProg.level_id);
            } else {
              progIDs.push(selectedProg.id);
            }
          });
          myPpl = myPpl.filter((m: any) => progIDs.includes(m?.program?.program_id) || levelIDs.includes(m?.program?.level_id));
          allPpl = allPpl.filter((m: any) => progIDs.includes(m?.program?.program_id) || levelIDs.includes(m?.program?.level_id));

          setFilteredMyPeople(myPpl);
          setFilteredAllPeople(allPpl);
        } else {
          clearFilters();
        }
        break;
      case "department":
        setSelectedDepartments(values);
        selected = departments.filter((p) => values.includes(p.key)).map((p) => p.name);

        if (selected.length > 0) {
          setBanFilterClicked(false);
          setSearchText("");

          setPeopleFiltered(true);
          myPpl = myPeople;
          allPpl = allPeople;
          myPpl = myPpl.filter((m: any) => selected.includes(m.department));
          allPpl = allPpl.filter((m: any) => selected.includes(m.department));

          setFilteredMyPeople(myPpl);
          setFilteredAllPeople(allPpl);
        } else {
          clearFilters();
        }
    }
  };

  const clearFilters = () => {
    //people view
    setFilteredMyPeople(myPeople);
    setFilteredAllPeople(allPeople);
    setPeopleFiltered(false);
    setBanFilterClicked(false);
    setPeopleFilter("");
    setSelectedPrograms([]);
    setSelectedDepartments([]);

    //case view
    setFilteredMyCases(myCases);
    setFilteredOtherCases(otherCases);
    setCasesFiltered(false);
    setCaseFilter("");
    setShowActivity(false);

    //both views
    setSearchText("");
  };

  const onClickFilterPeople = (type: string) => {
    clearFilters();
    setPeopleFilter(type);
  };

  const addMoreCases = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setOtherCasesLoaded(otherCasesLoaded + CASE_LOAD_AMOUNT);
    }, 1);
    setTimeout(() => {
      setLoadingMore(false);
    }, 1);
  };

  const addMorePeople = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setPeopleLoaded(peopleLoaded + PEOPLE_LOAD_AMOUNT);
    }, 1);
    setTimeout(() => {
      setLoadingMore(false);
    }, 1);
  };

  return (
    <>
      {isLoadingActive ? (
        <Container className={showMenu ? "lg div-main-menu custom-container px-5" : "lg div-main custom-container px-5"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container className={showMenu ? "lg div-main-menu custom-container px-5" : "lg div-main custom-container px-5"}>
          {auth?.profile?.role !== "support" && (
            <Row>
              <Col xs={6}>
                {viewActive ? (
                  <h2 className="hero-heading app-header-text">
                    {viewAllCases ? "All Cases (" + totalCases + ")" : "My Cases (" + myCases.length + ")"}
                  </h2>
                ) : (
                  <h2 className="hero-heading app-header-text">{"Inactive Cases (" + inactiveCases.length + ")"}</h2>
                )}
              </Col>
              <Col xs={6} className="d-flex align-items-end justify-content-end">
                {viewActive && !limitedAccess && (
                  <Button className="app-primary-color button me-2" onClick={(e) => onClickViewCases()}>
                    {viewAllCases ? "View My Cases" : "View All Cases"}
                  </Button>
                )}
                <Button
                  data-tooltip-id="tooltip-active"
                  data-tooltip-content={"Active"}
                  className={
                    viewActive
                      ? "app-primary-color button button-icon-filter rounded me-2"
                      : "app-primary-color-inverse button button-icon-filter rounded me-2"
                  }
                  onClick={() => onClickViewActive(true)}
                >
                  <FontAwesomeIcon icon={faBriefcase} />
                </Button>
                <Tooltip id="tooltip-active" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                <Button
                  data-tooltip-id="tooltip-inactive"
                  data-tooltip-content={"Inactive"}
                  className={
                    viewActive
                      ? "app-primary-color-inverse button button-icon-filter rounded me-2"
                      : "app-primary-color button button-icon-filter rounded me-2"
                  }
                  onClick={() => onClickViewActive(false)}
                  disabled={isMainLoading}
                >
                  <FontAwesomeIcon icon={faSquareXmark} />
                </Button>
                <Tooltip id="tooltip-inactive" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
              </Col>
            </Row>
          )}
          <Row className="mt-3 mb-4">
            <Col xs={0} lg={1}></Col>
            <Col xs={12} lg={10}>
              <InputGroup>
                {auth?.profile?.role !== "support" && (
                  <>
                    <small className="medium-text app-header-text me-2 align-self-center">View By</small>
                    <Button
                      data-tooltip-id="tooltip-case"
                      data-tooltip-content={"Case"}
                      className={
                        viewByCase
                          ? "app-primary-color button button-icon-filter rounded me-2"
                          : "app-primary-color-inverse button button-icon-filter rounded me-2"
                      }
                      onClick={() => onClickViewBy(true)}
                    >
                      <FontAwesomeIcon icon={faFolderOpen} />
                    </Button>
                    <Tooltip id="tooltip-case" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                    <Button
                      data-tooltip-id="tooltip-person"
                      data-tooltip-content={"Person"}
                      className={
                        !viewByCase
                          ? "app-primary-color button button-icon-filter rounded"
                          : "app-primary-color-inverse button button-icon-filter rounded"
                      }
                      onClick={(e) => onClickViewBy(false)}
                      disabled={isLoadingActive}
                    >
                      <FontAwesomeIcon icon={faUserFriends} />
                    </Button>
                    <Tooltip id="tooltip-person" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                  </>
                )}
                {viewActiveRef.current ? (
                  <Col>
                    <SearchBar
                      cssClass="search"
                      placeholder={isMainLoading ? "Waiting..." : viewByCase ? "Search Case Name or Person Name" : "Search Person Name"}
                      searchText={searchText}
                      disabled={isMainLoading}
                      setSearchText={setSearchText}
                      searchLogic={viewByCase ? onPerformSearchLogicByCase : onPerformSearchLogicByPerson}
                    />
                  </Col>
                ) : (
                  <Col>
                    <SearchBar
                      cssClass="search"
                      placeholder={isLoadingAll ? "Waiting..." : viewByCase ? "Search Case Name or Person Name" : "Search Person Name"}
                      searchText={searchText}
                      disabled={isLoadingAll}
                      setSearchText={setSearchText}
                      searchLogic={viewByCase ? onPerformSearchLogicByCase : onPerformSearchLogicByPerson}
                    />
                  </Col>
                )}
                {viewByCase ? (
                  <>
                    <div
                      data-tooltip-id="tooltip-activity"
                      data-tooltip-content={"Upcoming and Recent Activity"}
                      className="button-icon-filter me-2"
                    >
                      <Button
                        className={
                          showActivity
                            ? "app-primary-color button rounded me-2 w-100"
                            : viewActivityIndicator
                              ? "app-tertiary-color-inverse glow text-bold button rounded me-2 w-100"
                              : "app-primary-color-inverse button rounded me-2 w-100"
                        }
                        onClick={() => onClickShowActivity()}
                      >
                        <FontAwesomeIcon icon={showActivity ? faClose : faList} />
                        {viewActivityIndicator && (
                          <Badge bg="tertiary" className="app-tertiary-color position-absolute top-0 p-1 translate-middle">
                            New
                          </Badge>
                        )}
                      </Button>
                    </div>
                    <Tooltip id="tooltip-activity" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                    <div data-tooltip-id="tooltip-filter" data-tooltip-content={"Filter"}>
                      <DropdownButton
                        id={caseFilter ? "dropdown-filter-button-active" : "dropdown-filter-button"}
                        title={<FontAwesomeIcon icon={faFilter} />}
                      >
                        <Dropdown.Item
                          href="#/action-1"
                          className={caseFilter === "assessment" ? "app-tertiary-color-inverse" : ""}
                          onClick={() => onClickFilterCase("assessment")}
                        >
                          {`Assessment Due`}
                          {caseFilter === "assessment" ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#/action-1"
                          className={caseFilter === "messages" ? "app-tertiary-color-inverse" : ""}
                          onClick={() => onClickFilterCase("messages")}
                        >
                          {`Unread Messages`}
                          {caseFilter === "messages" ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#/action-1"
                          className={caseFilter === "task" ? "app-tertiary-color-inverse" : ""}
                          onClick={() => onClickFilterCase("task")}
                        >
                          {`Next Task Review`}
                          {caseFilter === "task" ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                        </Dropdown.Item>
                      </DropdownButton>
                    </div>
                    <Tooltip id="tooltip-filter" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                  </>
                ) : (
                  <>
                    <div data-tooltip-id="tooltip-ban" data-tooltip-content={"Banned"} className="button-icon-filter">
                      <Button
                        className={
                          banFilterClicked ? "app-primary-color button rounded me-2" : "app-primary-color-inverse button rounded me-2"
                        }
                        onClick={() => onClickFilterBan()}
                      >
                        <FontAwesomeIcon icon={faBan} />
                      </Button>
                    </div>
                    <Tooltip id="tooltip-ban" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                    <div data-tooltip-id="tooltip-filter" data-tooltip-content={"Filter"} style={{ marginLeft: "4px" }}>
                      <DropdownButton
                        id={peopleFilter ? "dropdown-filter-button-active" : "dropdown-filter-button"}
                        title={<FontAwesomeIcon icon={faFilter} />}
                      >
                        <Dropdown.Item href="#/action-1" onClick={() => onClickFilterPeople("program")}>
                          Programs
                        </Dropdown.Item>
                        <Dropdown.Item href="#/action-1" onClick={() => onClickFilterPeople("department")}>
                          Departments
                        </Dropdown.Item>
                      </DropdownButton>
                    </div>
                    <Tooltip id="tooltip-filter" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                  </>
                )}
              </InputGroup>
            </Col>
            <Col xs={0} lg={1}></Col>
          </Row>
          {peopleFilter && (
            <Row className="mt-3 mb-2">
              <Col></Col>
              <Col xs={6}>
                {peopleFilter === "program" && (
                  <SelectObjectTypeahead
                    label="Program / Level"
                    value={selectedPrograms}
                    options={programs}
                    onUpdate={(e: any) => onClickFilterBy(e)}
                    multiSelect={true}
                    focused={true}
                  />
                )}
                {peopleFilter === "department" && (
                  <SelectObjectTypeahead
                    label="Department"
                    value={selectedDepartments}
                    options={departments}
                    onUpdate={(e: any) => onClickFilterBy(e)}
                    multiSelect={true}
                    focused={true}
                  />
                )}
              </Col>
              <Col></Col>
            </Row>
          )}
          <>
            {viewByCase ? (
              <>
                {auth?.profile?.role !== "support" && viewActive && (
                  <MyCasesToday
                    cases={myCases}
                    otherCases={otherCases}
                    viewAll={viewAllCases}
                    caseFilter={caseFilter}
                    searchText={searchText}
                    onClickFilter={onClickFilterCase}
                    onClickClearFilters={clearFilters}
                  />
                )}
                <Row>
                  <Col xs={12} sm={12} md={12} lg={showActivity ? 6 : 12}>
                    <CaseView
                      filteredMyCases={filteredMyCases}
                      filteredOtherCases={filteredOtherCases}
                      caseStatus={viewActive ? "active" : "inactive"}
                      loadingMore={loadingMore}
                      otherCasesLoaded={otherCasesLoaded}
                      addMoreCases={addMoreCases}
                      limitedAccess={limitedAccess}
                      caseFilter={caseFilter}
                    />
                  </Col>
                  {showActivity && (
                    <Col xs={12} sm={12} md={12} lg={6}>
                      <MyCasesActivity activity={activity} />
                    </Col>
                  )}
                </Row>
              </>
            ) : (
              <PeopleView
                filteredAllPeople={filteredAllPeople}
                filteredMyPeople={filteredMyPeople}
                loadingMore={loadingMore}
                peopleLoaded={peopleLoaded}
                addMorePeople={addMorePeople}
                limitedAccess={limitedAccess}
              />
            )}
          </>
        </Container>
      )}
    </>
  );
};

export default MyCases;
